import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { jobRequisitionService } from "../jobRequisitionService";

/* ─── Helpers ─── */
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
};

const createInitialFormData = () => ({
  jobTitle: "",
  department: "",
  location: "",
  employmentType: "Full-time",
  jobLevel: "Mid Level",
  numberOfPositions: 1,
  priority: "Medium",
  reportingManager: "",
  experienceRequired: { min: 0, max: 0, preferred: "" },
  salaryRange: { min: 0, max: 0, currency: "INR" },
  budgetAllocated: 0,
  postingDate: "",
  closingDate: "",
  expectedJoiningDate: "",
  businessJustification: "",
  skills: [],
  qualifications: [],
  responsibilities: [],
});

/* ─── Section wrapper ─── */
const Section = ({ title, children, cardBg, border }) => (
  <Box bg={cardBg} border="1px solid" borderColor={border} borderRadius="xl" p={6}>
    <Text
      fontSize="xs"
      fontWeight="600"
      letterSpacing="wider"
      textTransform="uppercase"
      color="gray.400"
      mb={5}
    >
      {title}
    </Text>
    {children}
  </Box>
);

/* ─── Tag input ─── */
const TagInput = ({ label, items, onChange, placeholder, colorScheme = "blue" }) => {
  const [inputVal, setInputVal] = useState("");
  const inputRef = useRef(null);

  const addItem = (val) => {
    const trimmed = val.trim();
    if (!trimmed || items.includes(trimmed)) {
      setInputVal("");
      return;
    }
    onChange([...items, trimmed]);
    setInputVal("");
  };

  const removeItem = (item) => onChange(items.filter((i) => i !== item));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addItem(inputVal);
    } else if (e.key === "Backspace" && !inputVal && items.length > 0) {
      removeItem(items[items.length - 1]);
    }
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <Box
        border="1px solid"
        borderColor="inherit"
        borderRadius="md"
        p={2}
        minH="44px"
        cursor="text"
        onClick={() => inputRef.current?.focus()}
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        _focusWithin={{
          borderColor: "blue.400",
          boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
        }}
      >
        {items.map((item) => (
          <Tag key={item} size="sm" colorScheme={colorScheme} borderRadius="full" variant="subtle">
            <TagLabel>{item}</TagLabel>
            <TagCloseButton
              onClick={(e) => {
                e.stopPropagation();
                removeItem(item);
              }}
            />
          </Tag>
        ))}
        <Input
          ref={inputRef}
          variant="unstyled"
          size="sm"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (inputVal.trim()) addItem(inputVal);
          }}
          placeholder={items.length === 0 ? placeholder : ""}
          flex={1}
          minW="120px"
          fontSize="sm"
        />
      </Box>
      <Text fontSize="xs" color="gray.400" mt={1}>
        Press Enter or comma to add
      </Text>
    </FormControl>
  );
};

/* ─── Main Component ─── */
const JobRequisitionEditPage = () => {
  const { requisitionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(createInitialFormData());
  const [requisitionTitle, setRequisitionTitle] = useState("");

  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const subtleText = useColorModeValue("gray.500", "gray.400");

  const resolveRequisition = (res) =>
    res?.data?.requisition || res?.data || res?.requisition || res || null;

  const set = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  useEffect(() => {
    const fetchRequisition = async () => {
      try {
        setLoading(true);
        const response = await jobRequisitionService.getRequisitionById(requisitionId);
        const req = resolveRequisition(response);
        if (!req?._id) throw new Error("Not found");

        setRequisitionTitle(req.jobTitle || "Edit Requisition");
        setFormData({
          jobTitle: req.jobTitle || "",
          department: req.department || "",
          location: req.location || "",
          employmentType: req.employmentType || "Full-time",
          jobLevel: req.jobLevel || "Mid Level",
          numberOfPositions: req.numberOfPositions || 1,
          priority: req.priority || "Medium",
          reportingManager:
            typeof req.reportingManager === "object"
              ? req.reportingManager?.name || req.reportingManager?.firstName || ""
              : req.reportingManager || "",
          experienceRequired: req.experienceRequired || { min: 0, max: 0, preferred: "" },
          salaryRange: req.salaryRange || { min: 0, max: 0, currency: "INR" },
          budgetAllocated: req.budgetAllocated || 0,
          postingDate: formatDate(req.postingDate),
          closingDate: formatDate(req.closingDate),
          expectedJoiningDate: formatDate(req.expectedJoiningDate),
          businessJustification: req.businessJustification || "",
          skills: req.skills || [],
          qualifications: req.qualifications || [],
          responsibilities: req.responsibilities || [],
        });
      } catch {
        toast({ title: "Could not load requisition", status: "error", duration: 3000 });
        navigate("/recruiting/requisitions");
      } finally {
        setLoading(false);
      }
    };
    fetchRequisition();
  }, [requisitionId]); // eslint-disable-line

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.jobTitle || !formData.department || !formData.location) {
      toast({
        title: "Required fields missing",
        description: "Job title, department, and location are required.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setSaving(true);
      await jobRequisitionService.updateRequisition(requisitionId, formData);
      toast({ title: "Requisition saved", status: "success", duration: 2500 });
      navigate(`/recruiting/requisitions/${requisitionId}`);
    } catch {
      toast({ title: "Could not save requisition", status: "error", duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Flex h="60vh" align="center" justify="center" direction="column" gap={3}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
        <Text fontSize="sm" color={subtleText}>
          Loading…
        </Text>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg={bg}>
      {/* ── Header ── */}
      <Box
        bg={cardBg}
        borderBottom="1px solid"
        borderColor={border}
        px={{ base: 4, md: 8 }}
        py={4}
      >
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          size="sm"
          color={subtleText}
          px={0}
          mb={3}
          _hover={{ color: "blue.500" }}
          onClick={() => navigate(`/recruiting/requisitions/${requisitionId}`)}
        >
          Back to Details
        </Button>
        <Text fontSize="xl" fontWeight="700" letterSpacing="-0.02em">
          Edit — {requisitionTitle}
        </Text>
        <Text fontSize="sm" color={subtleText} mt={0.5}>
          Update position details, requirements, and compensation.
        </Text>
      </Box>

      {/* ── Form ── */}
      <Box as="form" onSubmit={handleSubmit} px={{ base: 4, md: 8 }} py={6} pb={32}>
        <VStack spacing={5} align="stretch" maxW="860px">

          {/* Basic Information */}
          <Section title="Basic Information" cardBg={cardBg} border={border}>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Job Title</FormLabel>
                <Input
                  value={formData.jobTitle}
                  onChange={(e) => set("jobTitle", e.target.value)}
                  placeholder="e.g. Senior Product Designer"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Department</FormLabel>
                <Input
                  value={formData.department}
                  onChange={(e) => set("department", e.target.value)}
                  placeholder="e.g. Engineering"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Location</FormLabel>
                <Input
                  value={formData.location}
                  onChange={(e) => set("location", e.target.value)}
                  placeholder="e.g. Bangalore / Remote"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Reporting Manager</FormLabel>
                <Input
                  value={formData.reportingManager}
                  onChange={(e) => set("reportingManager", e.target.value)}
                  placeholder="Manager name"
                />
              </FormControl>
            </Grid>
          </Section>

          {/* Role Details */}
          <Section title="Role Details" cardBg={cardBg} border={border}>
            <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm">Employment Type</FormLabel>
                <Select
                  value={formData.employmentType}
                  onChange={(e) => set("employmentType", e.target.value)}
                >
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Internship">Internship</option>
                  <option value="Freelance">Freelance</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Job Level</FormLabel>
                <Select
                  value={formData.jobLevel}
                  onChange={(e) => set("jobLevel", e.target.value)}
                >
                  <option value="Entry Level">Entry Level</option>
                  <option value="Mid Level">Mid Level</option>
                  <option value="Senior Level">Senior Level</option>
                  <option value="Lead">Lead</option>
                  <option value="Manager">Manager</option>
                  <option value="Director">Director</option>
                  <option value="VP">VP</option>
                  <option value="C-Level">C-Level</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Priority</FormLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => set("priority", e.target.value)}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">No. of Positions</FormLabel>
                <Input
                  type="number"
                  min={1}
                  value={formData.numberOfPositions}
                  onChange={(e) =>
                    set("numberOfPositions", Math.max(1, Number(e.target.value) || 1))
                  }
                />
              </FormControl>
            </Grid>
          </Section>

          {/* Experience */}
          <Section title="Experience Required" cardBg={cardBg} border={border}>
            <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(3, 1fr)" }} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm">Minimum (years)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={formData.experienceRequired.min}
                  onChange={(e) =>
                    set("experienceRequired.min", Number(e.target.value) || 0)
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Maximum (years)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={formData.experienceRequired.max}
                  onChange={(e) =>
                    set("experienceRequired.max", Number(e.target.value) || 0)
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Preferred Level</FormLabel>
                <Input
                  value={formData.experienceRequired.preferred}
                  onChange={(e) =>
                    set("experienceRequired.preferred", e.target.value)
                  }
                  placeholder="e.g. 4-5 years in fintech"
                />
              </FormControl>
            </Grid>
          </Section>

          {/* Compensation */}
          <Section title="Compensation" cardBg={cardBg} border={border}>
            <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm">Salary Min</FormLabel>
                <InputGroup size="sm">
                  <InputLeftAddon borderRadius="md">
                    {formData.salaryRange.currency}
                  </InputLeftAddon>
                  <Input
                    type="number"
                    min={0}
                    value={formData.salaryRange.min}
                    onChange={(e) =>
                      set("salaryRange.min", Number(e.target.value) || 0)
                    }
                    borderLeftRadius={0}
                  />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Salary Max</FormLabel>
                <InputGroup size="sm">
                  <InputLeftAddon borderRadius="md">
                    {formData.salaryRange.currency}
                  </InputLeftAddon>
                  <Input
                    type="number"
                    min={0}
                    value={formData.salaryRange.max}
                    onChange={(e) =>
                      set("salaryRange.max", Number(e.target.value) || 0)
                    }
                    borderLeftRadius={0}
                  />
                </InputGroup>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Currency</FormLabel>
                <Select
                  size="sm"
                  value={formData.salaryRange.currency}
                  onChange={(e) => set("salaryRange.currency", e.target.value)}
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="AED">AED</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Budget Allocated</FormLabel>
                <Input
                  type="number"
                  min={0}
                  size="sm"
                  value={formData.budgetAllocated}
                  onChange={(e) =>
                    set("budgetAllocated", Number(e.target.value) || 0)
                  }
                />
              </FormControl>
            </Grid>
          </Section>

          {/* Timeline */}
          <Section title="Timeline" cardBg={cardBg} border={border}>
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm">Posting Date</FormLabel>
                <Input
                  type="date"
                  value={formData.postingDate}
                  onChange={(e) => set("postingDate", e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Closing Date</FormLabel>
                <Input
                  type="date"
                  value={formData.closingDate}
                  onChange={(e) => set("closingDate", e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Expected Joining Date</FormLabel>
                <Input
                  type="date"
                  value={formData.expectedJoiningDate}
                  onChange={(e) => set("expectedJoiningDate", e.target.value)}
                />
              </FormControl>
            </Grid>
          </Section>

          {/* Job Requirements */}
          <Section title="Job Requirements" cardBg={cardBg} border={border}>
            <VStack spacing={5} align="stretch">
              <TagInput
                label="Skills"
                items={formData.skills}
                onChange={(val) => set("skills", val)}
                placeholder="Add skills (e.g. React, Python)…"
                colorScheme="blue"
              />
              <TagInput
                label="Qualifications"
                items={formData.qualifications}
                onChange={(val) => set("qualifications", val)}
                placeholder="Add qualifications (e.g. B.Tech, MBA)…"
                colorScheme="purple"
              />
              <TagInput
                label="Responsibilities"
                items={formData.responsibilities}
                onChange={(val) => set("responsibilities", val)}
                placeholder="Add responsibilities…"
                colorScheme="teal"
              />
            </VStack>
          </Section>

          {/* Business Justification */}
          <Section title="Business Justification" cardBg={cardBg} border={border}>
            <FormControl>
              <FormLabel fontSize="sm">Justification</FormLabel>
              <Textarea
                rows={5}
                value={formData.businessJustification}
                onChange={(e) => set("businessJustification", e.target.value)}
                placeholder="Explain why this position needs to be filled…"
                resize="vertical"
              />
            </FormControl>
          </Section>

        </VStack>
      </Box>

      {/* ── Sticky Footer ── */}
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg={cardBg}
        borderTop="1px solid"
        borderColor={border}
        px={{ base: 4, md: 8 }}
        py={3}
        zIndex={10}
      >
        <HStack justify="flex-end" spacing={3}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/recruiting/requisitions/${requisitionId}`)}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            size="sm"
            isLoading={saving}
            onClick={handleSubmit}
          >
            Save Changes
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default JobRequisitionEditPage;
